import React from "react";

/**
 * One stroke-icon set for the whole app.
 *
 * Every glyph is drawn on the same 24x24 grid with the same stroke weight and
 * round caps, and inherits `currentColor`. Sizing is left to CSS so a single
 * rule can scale icons per context instead of every call site passing a width.
 */
const base = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": "true",
  focusable: "false",
};

const make = (name, children) => {
  const C = (props) => (
    <svg {...base} {...props}>
      {children}
    </svg>
  );
  C.displayName = `Icon.${name}`;
  return C;
};

export const Icon = {
  // --- Value / balance ---
  Bolt: make("bolt", <path d="M13 2 4.5 13.5H11l-1 8.5 9.5-11.5H13z" />),
  Coin: make(
    "coin",
    <>
      <ellipse cx="12" cy="6.5" rx="7.5" ry="3.2" />
      <path d="M4.5 6.5v5c0 1.8 3.4 3.2 7.5 3.2s7.5-1.4 7.5-3.2v-5" />
      <path d="M4.5 11.5v5c0 1.8 3.4 3.2 7.5 3.2s7.5-1.4 7.5-3.2v-5" />
    </>
  ),
  Spark: make(
    "spark",
    <path d="M12 3v3.5M12 17.5V21M3 12h3.5M17.5 12H21M5.6 5.6l2.5 2.5M15.9 15.9l2.5 2.5M18.4 5.6l-2.5 2.5M8.1 15.9l-2.5 2.5" />
  ),

  // --- Actions ---
  Download: make(
    "download",
    <>
      <path d="M12 3.5v11m0 0 4-4m-4 4-4-4" />
      <path d="M4 16.5v2A2.5 2.5 0 0 0 6.5 21h11a2.5 2.5 0 0 0 2.5-2.5v-2" />
    </>
  ),
  Copy: make(
    "copy",
    <>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15H4.5A1.5 1.5 0 0 1 3 13.5v-9A1.5 1.5 0 0 1 4.5 3h9A1.5 1.5 0 0 1 15 4.5V5" />
    </>
  ),
  Check: make("check", <path d="M20 6 9 17l-5-5" />),
  Refresh: make(
    "refresh",
    <>
      <path d="M20 11a8 8 0 1 0-2.3 5.7" />
      <path d="M20 4.5V11h-6.5" />
    </>
  ),
  Sliders: make(
    "sliders",
    <>
      <path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h10M18 18h2" />
      <circle cx="16" cy="6" r="2" />
      <circle cx="10" cy="12" r="2" />
      <circle cx="16" cy="18" r="2" />
    </>
  ),
  External: make(
    "external",
    <>
      <path d="M14 4h6v6" />
      <path d="M20 4 11 13" />
      <path d="M18 14v4.5A1.5 1.5 0 0 1 16.5 20h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6H10" />
    </>
  ),

  // --- State ---
  Lock: make(
    "lock",
    <>
      <rect x="4" y="10.5" width="16" height="10" rx="2" />
      <path d="M8 10.5V7.6a4 4 0 0 1 8 0v2.9" />
    </>
  ),
  Eye: make(
    "eye",
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  Clock: make(
    "clock",
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5.2l3.2 2" />
    </>
  ),
  Warn: make(
    "warn",
    <>
      <path d="M12 3.2 2.6 19.4h18.8z" />
      <path d="M12 10v4M12 16.6v.1" />
    </>
  ),

  // --- Objects ---
  Card: make(
    "card",
    <>
      <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
      <path d="M2.5 10h19" />
      <path d="M6.5 14.5h4" />
    </>
  ),
  Users: make(
    "users",
    <>
      <circle cx="9" cy="8" r="3.4" />
      <path d="M2.8 20a6.2 6.2 0 0 1 12.4 0" />
      <path d="M16.5 5.2a3.4 3.4 0 0 1 0 6.6M17.5 14.4A6.2 6.2 0 0 1 21.2 20" />
    </>
  ),
  Gift: make(
    "gift",
    <>
      <rect x="3" y="8.5" width="18" height="12" rx="2" />
      <path d="M3 12.5h18M12 8.5V20.5" />
      <path d="M12 8.5S10.5 3.5 8 3.5a2.5 2.5 0 0 0 0 5zM12 8.5s1.5-5 4-5a2.5 2.5 0 0 1 0 5z" />
    </>
  ),
  Ticket: make(
    "ticket",
    <>
      <path d="M3 9V6.5A1.5 1.5 0 0 1 4.5 5h15A1.5 1.5 0 0 1 21 6.5V9a3 3 0 0 0 0 6v2.5a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5V15a3 3 0 0 0 0-6z" />
      <path d="M14 5v14" strokeDasharray="2 2.5" />
    </>
  ),
  Key: make(
    "key",
    <>
      <circle cx="8" cy="15" r="4.2" />
      <path d="M11 12 20.5 2.5M17 6l2.5 2.5M14.5 8.5 17 11" />
    </>
  ),
  Image: make(
    "image",
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="8.5" cy="10" r="1.6" />
      <path d="m3.5 17 5-4 3.5 2.8 3.5-3L20.5 16" />
    </>
  ),

  // --- Roles / plans ---
  Person: make(
    "person",
    <>
      <circle cx="12" cy="7.5" r="3.6" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </>
  ),
  Headset: make(
    "headset",
    <>
      <path d="M4 13v-1a8 8 0 0 1 16 0v1" />
      <rect x="3" y="13" width="4" height="6" rx="1.6" />
      <rect x="17" y="13" width="4" height="6" rx="1.6" />
      <path d="M20 19v1a2.5 2.5 0 0 1-2.5 2.5H14" />
    </>
  ),
  Shield: make(
    "shield",
    <path d="M12 3.2 19 6v5.5c0 4.6-3 7.9-7 9.3-4-1.4-7-4.7-7-9.3V6z" />
  ),
  Flask: make(
    "flask",
    <>
      <path d="M10 3h4M10 3v6.2L4.9 18a1.8 1.8 0 0 0 1.55 2.7h11.1A1.8 1.8 0 0 0 19.1 18L14 9.2V3" />
      <path d="M8 15h8" />
    </>
  ),
  Bug: make(
    "bug",
    <>
      <rect x="8" y="8.5" width="8" height="9.5" rx="4" />
      <path d="M12 8.5V6M9.5 6.8 8 5M14.5 6.8 16 5" />
      <path d="M4.5 11h3.2M16.3 11h3.2M4.5 14.5h3.2M16.3 14.5h3.2M9 18.5l-2 2.3M15 18.5l2 2.3" />
    </>
  ),
  GitBranch: make(
    "git-branch",
    <>
      <circle cx="6" cy="5.5" r="2.2" />
      <circle cx="6" cy="18.5" r="2.2" />
      <circle cx="18" cy="9" r="2.2" />
      <path d="M6 7.7v8.6M6 9a9 9 0 0 0 9.4-.9" />
    </>
  ),
  Bell: make(
    "bell",
    <>
      <path d="M6.5 10.3a5.5 5.5 0 0 1 11 0v3.1c0 .9.3 1.8 1 2.5l.3.4a1 1 0 0 1-.8 1.6H6a1 1 0 0 1-.8-1.6l.3-.4c.7-.7 1-1.6 1-2.5z" />
      <path d="M9.6 19.8a2.6 2.6 0 0 0 4.8 0" />
    </>
  ),
  BellOff: make(
    "bell-off",
    <>
      <path d="M6.5 10.3a5.5 5.5 0 0 1 8.6-4.5M17.5 10.3v3.1c0 .9.3 1.8 1 2.5l.3.4a1 1 0 0 1-.8 1.6H6" />
      <path d="M9.6 19.8a2.6 2.6 0 0 0 4.8 0" />
      <path d="M3.5 3.5l17 17" />
    </>
  ),
  DoubleCheck: make(
    "double-check",
    <path d="m1.5 12.5 4 4L13 8.5M9 12.5l4 4 7.5-9.5" />
  ),
  ChevronDown: make("chevron-down", <path d="m6 9 6 6 6-6" />),
};

export default Icon;
